import { Autocomplete, Grid, TextField, Typography } from '@mui/material'
import { connect, FormikContextType, getIn } from 'formik'
import { CheckCircle } from 'mdi-material-ui'
import * as React from 'react'
import { CSSProperties } from 'react'
import { ILocation, IPatient } from 'src/Definitions'
import FullWidthField from '../../common/FullWidthField'
import States from '../../utils/States'
import PhoneNumberMask from '../PhoneNumberMask'
import { IAddressFormValues } from './AddressFormValues'

export interface IHaveAddressFormValues {
  address?: IAddressFormValues
  billingAddress?: IAddressFormValues
  insuredAddress?: IAddressFormValues
  patientAddress?: IAddressFormValues
  physicalAddress?: IAddressFormValues
  billingInquiries?: IAddressFormValues
  generalInquiries?: IAddressFormValues
  isBillingAddressSameAsPhysicalAddress?: boolean
}

interface IFormikProps {
  formik: FormikContextType<IHaveAddressFormValues>
}

interface IAddressFieldProps {
  disabled?: boolean
  header?: JSX.Element
  label?: string
  name: string
  handleParentChange?: (dist: boolean) => void
  showPhone?: boolean
  showEmail?: boolean
  showFax?: boolean
  showWebsite?: boolean
  outlined?: boolean
  required?: boolean
  requireWebsite?: boolean
  requireFax?: boolean
  requirePhone?: boolean
  labelIcon?: JSX.Element
  largeLabel?: boolean
  setStyle?: CSSProperties
  addressLabel?: string
  verified?: boolean
  resetCoordinates?: () => void
  location?: ILocation
  patient?: IPatient
  previousValues?: string
  setPrevious?: (vals: string) => void
}

const AddressField: React.FC<IAddressFieldProps & IFormikProps> = (props) => {
  const {
    disabled,
    formik,
    showPhone,
    showEmail,
    showFax,
    showWebsite,
    outlined,
    required,
    requireWebsite,
    requireFax,
    requirePhone,
    labelIcon,
    largeLabel,
    setStyle,
    addressLabel,
    verified,
    resetCoordinates,
    location,
    patient,
    previousValues,
    setPrevious,
  } = props

  const createName = (field: string) => `${props.name}.${field}`
  const hasValue = (field: string) => {
    const name = createName(field)
    const value = getIn(formik.values, name)
    return !!value
  }

  const stateValue = getIn(formik.values, createName('state'))

  const getValue = (fieldName: string) => {
    const name = createName(fieldName)
    const value = getIn(formik.values, name)
    return value
  }

  React.useEffect(() => {
    if (props.handleParentChange) {
      props.handleParentChange!(
        !(hasValue('city') || hasValue('state') || hasValue('zip'))
      )
    }

    const actualState = getValue('state') || ''
    const currentAddressString =
      getValue('line1') + getValue('city') + actualState + getValue('zip')

    if (location && location?.physicalAddress) {
      if (
        resetCoordinates &&
        setPrevious &&
        currentAddressString != previousValues &&
        (getValue('line1') != location?.physicalAddress.line1 ||
          getValue('city') != location?.physicalAddress.city ||
          actualState != location?.physicalAddress.state ||
          getValue('zip') != location?.physicalAddress.zip)
      ) {
        resetCoordinates()
        setPrevious!(currentAddressString)
      }
    } else if (patient && patient?.address) {
      if (
        resetCoordinates &&
        setPrevious &&
        currentAddressString != previousValues &&
        (getValue('line1') != patient?.address.line1 ||
          getValue('city') != patient?.address.city ||
          actualState != patient?.address.state ||
          getValue('zip') != patient?.address.zip)
      ) {
        resetCoordinates()
        setPrevious!(currentAddressString)
      }
    }
  }, [
    getIn(formik.values, createName('line1')),
    getIn(formik.values, createName('city')),
    getIn(formik.values, createName('state')),
    getIn(formik.values, createName('zip')),
  ])

  return (
    <Grid container direction="column" spacing={2} wrap="nowrap">
      {props.label && (
        <Grid
          item
          container
          direction="row"
          spacing={0}
          alignItems="center"
          style={setStyle}
        >
          {labelIcon ? <Grid item>{labelIcon}</Grid> : <></>}
          <Grid item style={setStyle}>
            <Typography
              variant={largeLabel ? 'h6' : 'body1'}
              color="textSecondary"
              style={{ height: '18px', paddingRight: '8px' }}
            >
              {props.label}
            </Typography>
          </Grid>
          <Grid item style={{ paddingTop: '10px' }}>
            {props.header}
            {verified == true ? <CheckCircle color="primary" /> : undefined}
          </Grid>
        </Grid>
      )}

      <Grid item>
        <FullWidthField
          name={createName('line1')}
          disabled={disabled}
          label={addressLabel ?? 'Street Address'}
          InputLabelProps={{ shrink: true }}
          variant={outlined ? 'outlined' : 'standard'}
          required={required}
          errorMessage={getIn(formik.errors, createName('line1'))}
          InputProps={{ style: { height: '40px' } }}
        />
      </Grid>
      <Grid item>
        <FullWidthField
          name={createName('line2')}
          disabled={disabled}
          label={addressLabel ? addressLabel + ' 2' : 'Street Address 2'}
          InputLabelProps={{ shrink: true }}
          variant={outlined ? 'outlined' : 'standard'}
          errorMessage={getIn(formik.errors, createName('line2'))}
          InputProps={{ style: { height: '40px' } }}
        />
      </Grid>
      <Grid item container direction="row" spacing={2} style={{ marginBottom: 8 }}>
        <Grid item md={6}>
          <FullWidthField
            name={createName('city')}
            disabled={disabled}
            label="City"
            InputLabelProps={{ shrink: true }}
            variant={outlined ? 'outlined' : 'standard'}
            required={required}
            InputProps={{ style: { height: '40px' } }}
          />
        </Grid>
        <Grid item md={3}>
          <Autocomplete
            size="small"
            id={createName('state')}
            options={States}
            getOptionLabel={(option) => (option && option.name ? option.name : '')}
            value={stateValue ? States.find((s) => s.name === stateValue) || null : null}
            onChange={(_, value) =>
              formik.setFieldValue(createName('state'), value?.name || '')
            }
            isOptionEqualToValue={(option, value) => option.name === value?.name}
            disabled={disabled}
            fullWidth
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="State"
                variant={outlined ? 'outlined' : 'standard'}
                required={required}
                error={!!getIn(formik.errors, createName('state'))}
                helperText={getIn(formik.errors, createName('state'))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: { height: '40px' },
                }}
              />
            )}
          />
        </Grid>
        <Grid item md={3}>
          <FullWidthField
            name={createName('zip')}
            disabled={disabled}
            label="Zip Code"
            InputLabelProps={{ shrink: true }}
            variant={outlined ? 'outlined' : 'standard'}
            required={required}
            errorMessage={getIn(formik.errors, createName('zip'))}
            InputProps={{ style: { height: '40px' } }}
          />
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing={2}>
        {showPhone == null || showPhone ? (
          <Grid item md={3}>
            <FullWidthField
              name={createName('phoneNumber')}
              disabled={disabled}
              label="Phone #"
              required={requirePhone ?? false}
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputComponent: PhoneNumberMask, style: { height: '40px' } }}
              variant={outlined ? 'outlined' : 'standard'}
              errorMessage={getIn(formik.errors, createName('phoneNumber'))}
            />
          </Grid>
        ) : (
          <></>
        )}
        {showFax ? (
          <Grid item md={3}>
            <FullWidthField
              name={createName('faxNumber')}
              disabled={disabled}
              label="Fax #"
              required={requireFax ?? false}
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputComponent: PhoneNumberMask, style: { height: '40px' } }}
              variant={outlined ? 'outlined' : 'standard'}
              errorMessage={getIn(formik.errors, createName('faxNumber'))}
            />
          </Grid>
        ) : (
          <></>
        )}
        {showWebsite ? (
          <Grid item md={3}>
            <FullWidthField
              name={createName('website')}
              disabled={disabled}
              label="Website"
              InputLabelProps={{ shrink: true }}
              variant={outlined ? 'outlined' : 'standard'}
              required={requireWebsite !== undefined ? requireWebsite : required}
              errorMessage={getIn(formik.errors, createName('website'))}
              InputProps={{ style: { height: '40px' } }}
            />
          </Grid>
        ) : (
          <></>
        )}
        {showEmail ? (
          <Grid item md={3}>
            <FullWidthField
              name={createName('email')}
              disabled={disabled}
              label="Email"
              InputLabelProps={{ shrink: true }}
              variant={outlined ? 'outlined' : 'standard'}
              required={required}
              errorMessage={getIn(formik.errors, createName('email'))}
              InputProps={{ style: { height: '40px' } }}
            />
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  )
}

export default connect<IAddressFieldProps, IHaveAddressFormValues>(AddressField)
